/**
 * Booking sheet state lives in the URL (?book=step&svc=…&day=…&at=…) so the phone's
 * Back button steps back through the sheet and deep links can open it.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

export type BookStep = 'service' | 'time' | 'contacts' | 'confirm' | 'done';
const STEPS: BookStep[] = ['service', 'time', 'contacts', 'confirm', 'done'];
const KEYS = ['book', 'svc', 'day', 'at', 'bid'];

/** React Router keeps a monotonically increasing index in history.state. */
const historyIdx = (): number | null => {
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
};

/** History index of the page the sheet was opened over (null = opened by deep link). One sheet per app. */
const baseIdx: { current: number | null } = { current: null };

export function useBookingNav() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const stripAfterPop = useRef(false);

  const step = (STEPS as string[]).includes(params.get('book') ?? '') ? (params.get('book') as BookStep) : null;

  const strip = useCallback(() => {
    const p = new URLSearchParams(params);
    KEYS.forEach((k) => p.delete(k));
    setParams(p, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (!step) baseIdx.current = null;
    if (stripAfterPop.current) {
      stripAfterPop.current = false;
      if (step) strip();
    }
  }, [step, strip]);

  const go = useCallback(
    (
      next: Partial<{ step: BookStep; serviceId: string | null; day: string | null; startsAt: string | null; bookingId: string | null }>,
      opts: { replace?: boolean } = {},
    ) => {
      const p = new URLSearchParams(params);
      const set = (k: string, v: string | null | undefined) => (v ? p.set(k, v) : p.delete(k));
      if (next.step !== undefined) set('book', next.step);
      if (next.serviceId !== undefined) set('svc', next.serviceId);
      if (next.day !== undefined) set('day', next.day);
      if (next.startsAt !== undefined) set('at', next.startsAt);
      if (next.bookingId !== undefined) set('bid', next.bookingId);
      if (!step && !opts.replace) baseIdx.current = historyIdx();
      setParams(p, { replace: Boolean(opts.replace) });
    },
    [params, setParams, step],
  );

  /** Close the sheet: unwind the entries it pushed so Back does not reopen it. */
  const close = useCallback(() => {
    const cur = historyIdx();
    const base = baseIdx.current;
    baseIdx.current = null;
    if (base !== null && cur !== null && cur > base) {
      stripAfterPop.current = true;
      navigate(base - cur);
      return;
    }
    strip();
  }, [navigate, strip]);

  return {
    step,
    serviceId: params.get('svc'),
    day: params.get('day'),
    startsAt: params.get('at'),
    bookingId: params.get('bid'),
    go,
    close,
  };
}

/** Opens the booking sheet from anywhere (home CTA, service row, assistant suggestion). */
export function useOpenBooking() {
  const [params, setParams] = useSearchParams();
  return useCallback(
    (opts: { serviceId?: string; startsAt?: string; day?: string } = {}) => {
      if (!params.get('book')) baseIdx.current = historyIdx();
      setParams(new URLSearchParams(bookingSearch(opts)));
    },
    [params, setParams],
  );
}

/** Search string that opens the booking sheet. */
export function bookingSearch(opts: { serviceId?: string; startsAt?: string; day?: string } = {}) {
  const p = new URLSearchParams();
  if (opts.startsAt && opts.serviceId) {
    p.set('book', 'contacts');
    p.set('svc', opts.serviceId);
    p.set('at', opts.startsAt);
    if (opts.day) p.set('day', opts.day);
  } else if (opts.serviceId) {
    p.set('book', 'time');
    p.set('svc', opts.serviceId);
  } else p.set('book', 'service');
  return `?${p}`;
}
